import type {
  ClusterBranchSide,
  ClusterNodeData,
  TreeModel,
  TreeNode
} from '../types';
import type { Edge, Node } from '@xyflow/react';

const NODE_WIDTH = 250;
/** Room for up to 2-line labels (see ClusterNode WebkitLineClamp). */
const NODE_HEIGHT = 88;
const NODE_HEIGHT_WITH_BADGES = 116;
const COMPACT_NODE_WIDTH = 190;
const COMPACT_NODE_HEIGHT = 56;
const COMPACT_NODE_HEIGHT_WITH_BADGES = 78;
const H_GAP = 72;
const V_GAP = 20;
const V_GAP_WITH_BADGES = 22;
const COMPACT_H_GAP = 48;
const COMPACT_V_GAP = 14;
const COMPACT_V_GAP_WITH_BADGES = 16;

export type LayoutOptions = {
  compact?: boolean;
  showBadges?: boolean;
  maxDepth?: number | null;
};

export type LayoutResult = {
  nodes: Array<Node<ClusterNodeData>>;
  edges: Edge[];
};

function countVisibleLeaves(
  nodesById: Map<string, TreeNode>,
  id: string,
  expandedIds: Set<string>,
  maxDepth: number | null
): number {
  const node = nodesById.get(id);
  if (!node) return 0;
  const depthLimited = maxDepth != null && node.depth >= maxDepth;
  const isExpanded = expandedIds.has(id) && !depthLimited;
  if (node.isLeaf || !isExpanded || node.childIds.length === 0) return 1;
  return node.childIds.reduce(
    (sum, childId) =>
      sum + countVisibleLeaves(nodesById, childId, expandedIds, maxDepth),
    0
  );
}

function getVisibleMaxDepth(
  nodesById: Map<string, TreeNode>,
  id: string,
  expandedIds: Set<string>,
  maxDepth: number | null,
  depth = 0
): number {
  const node = nodesById.get(id);
  if (!node) return depth;
  const depthLimited = maxDepth != null && node.depth >= maxDepth;
  if (
    node.isLeaf ||
    !expandedIds.has(id) ||
    depthLimited ||
    node.childIds.length === 0
  ) {
    return depth;
  }
  return node.childIds.reduce(
    (max, childId) =>
      Math.max(
        max,
        getVisibleMaxDepth(nodesById, childId, expandedIds, maxDepth, depth + 1)
      ),
    depth
  );
}

function subtreeHeight(
  nodesById: Map<string, TreeNode>,
  id: string,
  expandedIds: Set<string>,
  maxDepth: number | null,
  nodeHeight: number,
  vGap: number
): number {
  const leaves = countVisibleLeaves(nodesById, id, expandedIds, maxDepth);
  if (leaves <= 0) return 0;
  return leaves * nodeHeight + (leaves - 1) * vGap;
}

function stackHeight(
  nodesById: Map<string, TreeNode>,
  ids: string[],
  expandedIds: Set<string>,
  maxDepth: number | null,
  nodeHeight: number,
  vGap: number
): number {
  if (ids.length === 0) return 0;
  return ids.reduce((sum, id, index) => {
    const h = subtreeHeight(
      nodesById,
      id,
      expandedIds,
      maxDepth,
      nodeHeight,
      vGap
    );
    return sum + h + (index > 0 ? vGap : 0);
  }, 0);
}

/**
 * Bidirectional layout: root (main) in the center,
 * half of level-1 clusters open LEFT, half open RIGHT.
 * Deeper levels keep expanding in their branch direction.
 */
export function layoutVisibleTree(
  model: TreeModel,
  expandedIds: Set<string>,
  options: LayoutOptions = {}
): LayoutResult {
  const { nodesById, rootId } = model;
  const compact = Boolean(options.compact);
  const showBadges = options.showBadges !== false;
  const maxDepth = options.maxDepth ?? null;
  const nodeWidth = compact ? COMPACT_NODE_WIDTH : NODE_WIDTH;
  const nodeHeight = compact
    ? showBadges
      ? COMPACT_NODE_HEIGHT_WITH_BADGES
      : COMPACT_NODE_HEIGHT
    : showBadges
      ? NODE_HEIGHT_WITH_BADGES
      : NODE_HEIGHT;
  const hGap = compact ? COMPACT_H_GAP : H_GAP;
  const vGap = compact
    ? showBadges
      ? COMPACT_V_GAP_WITH_BADGES
      : COMPACT_V_GAP
    : showBadges
      ? V_GAP_WITH_BADGES
      : V_GAP;

  const nodes: Array<Node<ClusterNodeData>> = [];
  const edges: Edge[] = [];
  if (!rootId) return { nodes, edges };

  const root = nodesById.get(rootId);
  if (!root) return { nodes, edges };
  if (maxDepth != null && root.depth > maxDepth) return { nodes, edges };

  const stepX = nodeWidth + hGap;
  const rootDepthLimited = maxDepth != null && root.depth >= maxDepth;
  const rootExpanded = expandedIds.has(rootId) && !rootDepthLimited;

  const visibleChildIds = rootExpanded
    ? root.childIds.filter(childId => {
        const child = nodesById.get(childId);
        if (!child) return false;
        if (maxDepth != null && child.depth > maxDepth) return false;
        return true;
      })
    : [];

  // Split level-1 clusters across left / right of main.
  const splitAt = Math.ceil(visibleChildIds.length / 2);
  const leftChildIds = visibleChildIds.slice(0, splitAt);
  const rightChildIds = visibleChildIds.slice(splitAt);

  const leftMaxDepth = leftChildIds.reduce(
    (max, id) =>
      Math.max(max, getVisibleMaxDepth(nodesById, id, expandedIds, maxDepth)),
    0
  );
  const leftSpan = leftChildIds.length > 0 ? leftMaxDepth + 1 : 0;
  const rootX = leftSpan * stepX;

  const leftStackH = stackHeight(
    nodesById,
    leftChildIds,
    expandedIds,
    maxDepth,
    nodeHeight,
    vGap
  );
  const rightStackH = stackHeight(
    nodesById,
    rightChildIds,
    expandedIds,
    maxDepth,
    nodeHeight,
    vGap
  );
  const totalHeight = Math.max(leftStackH, rightStackH, nodeHeight);
  const rootY = totalHeight / 2 - nodeHeight / 2;

  function pushNode(
    id: string,
    x: number,
    y: number,
    branchSide: ClusterBranchSide
  ): void {
    const node = nodesById.get(id);
    if (!node) return;
    const depthLimited = maxDepth != null && node.depth >= maxDepth;
    const isExpanded = expandedIds.has(id) && !depthLimited;
    const hasHiddenChildren = !node.isLeaf && !isExpanded;

    nodes.push({
      id,
      type: 'cluster',
      position: { x, y },
      draggable: true,
      data: {
        label: node.label,
        depth: node.depth,
        isLeaf: node.isLeaf,
        childCount: node.childIds.length,
        rawName: node.rawName,
        path: node.path,
        seo: node.seo,
        isExpanded,
        hasHiddenChildren,
        canExpand: !node.isLeaf && !depthLimited,
        compact,
        showBadges,
        branchSide
      },
      style: { width: nodeWidth, height: nodeHeight }
    });
  }

  function pushEdge(
    parentId: string,
    childId: string,
    side: 'left' | 'right'
  ): void {
    const sourceHandle = side === 'left' ? 'out-left' : 'out-right';
    const targetHandle = side === 'left' ? 'in-right' : 'in-left';
    edges.push({
      id: `e-${parentId}-${childId}`,
      source: parentId,
      target: childId,
      sourceHandle,
      targetHandle,
      type: 'smoothstep',
      className: 'cluster-edge',
      style: { stroke: '#94a3b8', strokeWidth: 1.5 }
    });
  }

  function walk(
    id: string,
    yOffset: number,
    side: 'left' | 'right'
  ): void {
    const node = nodesById.get(id);
    if (!node) return;
    if (maxDepth != null && node.depth > maxDepth) return;

    const leafCount = countVisibleLeaves(nodesById, id, expandedIds, maxDepth);
    const height = leafCount * nodeHeight + (leafCount - 1) * vGap;
    const x =
      side === 'left'
        ? rootX - node.depth * stepX
        : rootX + node.depth * stepX;
    const y = yOffset + height / 2 - nodeHeight / 2;
    const depthLimited = maxDepth != null && node.depth >= maxDepth;
    const isExpanded = expandedIds.has(id) && !depthLimited;

    pushNode(id, x, y, side);

    if (!node.isLeaf && isExpanded) {
      let childOffset = yOffset;
      node.childIds.forEach(childId => {
        const child = nodesById.get(childId);
        if (!child) return;
        if (maxDepth != null && child.depth > maxDepth) return;

        const childH = subtreeHeight(
          nodesById,
          childId,
          expandedIds,
          maxDepth,
          nodeHeight,
          vGap
        );
        walk(childId, childOffset, side);
        pushEdge(id, childId, side);
        childOffset += childH + vGap;
      });
    }
  }

  function layoutSide(
    childIds: string[],
    side: 'left' | 'right',
    stackH: number
  ): void {
    let childOffset = (totalHeight - stackH) / 2;
    childIds.forEach(childId => {
      const childH = subtreeHeight(
        nodesById,
        childId,
        expandedIds,
        maxDepth,
        nodeHeight,
        vGap
      );
      walk(childId, childOffset, side);
      pushEdge(rootId, childId, side);
      childOffset += childH + vGap;
    });
  }

  pushNode(rootId, rootX, rootY, 'center');
  layoutSide(leftChildIds, 'left', leftStackH);
  layoutSide(rightChildIds, 'right', rightStackH);

  return { nodes, edges };
}
